import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  makeStyles,
  ButtonBase,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import { formattedCurrency, formatStockAmount, capitalize } from '../../../utils/utils';
import { Autocomplete } from '@material-ui/lab';
import useApi from '../../../utils/useApi';
import ProductUnitSelect from './ProductUnitSelect';
import UIBarcodeRegenerateButton from '../../UI/Barcode/UIBarcodeRegenerateButton';
import { normalizeUnitCode } from '../../../constants/productUnits';

function safeParseParticulars(val) {
  if (!val) return {};
  if (typeof val === 'object') return val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed || trimmed === '{}') return {};
    try {
      return JSON.parse(trimmed);
    } catch (e) {
      try {
        return JSON.parse(trimmed.replace(/'/g, '"'));
      } catch (e2) {
        return {};
      }
    }
  }
  return {};
}

const useStyles = makeStyles((theme) => ({
  infoBox: {
    backgroundColor: '#f4f7fb',
    border: '1px solid #dbe4ef',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  segmentedContainer: {
    display: 'flex',
    backgroundColor: '#eef2f6',
    borderRadius: 8,
    padding: 3,
    marginBottom: theme.spacing(2),
    border: '1px solid #dce4ec',
  },
  segmentedButton: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#64748b',
    transition: 'all 0.2s ease',
  },
  segmentedButtonActive: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#ffffff',
    color: '#17497F',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  formulaBox: {
    backgroundColor: '#e8f4fd',
    border: '1px solid #b6dcfb',
    borderRadius: 6,
    padding: '8px 12px',
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
}));

function extractVolumeOrWeight(name, pUnit) {
  if (name) {
    const match = name.match(/(\d+(?:[.,]\d+)?)\s*(ml|l|litros?|g|kg|gramas?|quilos?)\b/i);
    if (match) {
      const rawNum = parseFloat(match[1].replace(',', '.'));
      const unit = match[2].toLowerCase();
      if (unit.startsWith('l')) {
        return { total: rawNum * 1000, unit: 'ml' };
      }
      if (unit === 'kg' || unit.startsWith('quilo')) {
        return { total: rawNum * 1000, unit: 'g' };
      }
      return { total: rawNum, unit: unit.startsWith('m') ? 'ml' : 'g' };
    }
  }
  if (pUnit) {
    const u = pUnit.trim().toLowerCase();
    if (u === 'l' || u === 'lt' || u === 'litro' || u === 'litros') {
      return { total: 1000, unit: 'ml' };
    }
    if (u === 'ml') {
      return { total: 1000, unit: 'ml' };
    }
    if (u === 'kg' || u === 'kilo' || u === 'quilo') {
      return { total: 1000, unit: 'g' };
    }
    if (u === 'g' || u === 'gr' || u === 'grama' || u === 'gramas') {
      return { total: 1000, unit: 'g' };
    }
  }
  return null;
}

const ProductSaleFormatModal = ({
  open,
  onClose,
  onSave,
  initialData,
  parentCost = 0,
  parentName = '',
  parentUnit = '',
  suggestedFactor = null,
  category = null,
  categories = null,
  onBarcodeRegenerated,
}) => {
  const classes = useStyles();

  const [categoriesLoad, categoriesInfo] = useApi({
    method: 'get',
    url: '/products/categories',
  });

  useEffect(() => {
    if (open && (!categories || categories.length === 0)) {
      categoriesLoad({ params: { isActive: 1 } });
    }
  }, [open, categories]);

  const allCategories = useMemo(() => {
    if (categories && categories.length > 0) return categories;
    return categoriesInfo.data || [];
  }, [categories, categoriesInfo.data]);

  const resolveCategory = (catRef, list) => {
    if (!catRef) return null;
    const catId = typeof catRef === 'object' ? catRef.id : catRef;
    if (list && list.length > 0) {
      const found = list.find((c) => String(c.id) === String(catId));
      if (found) return found;
    }
    return typeof catRef === 'object' ? catRef : null;
  };

  const [selectedCategory, setSelectedCategory] = useState(null);

  const categoryParticularsDef = useMemo(() => {
    return safeParseParticulars(selectedCategory?.particulars);
  }, [selectedCategory]);

  const [mode, setMode] = useState('UNIDADES'); // 'UNIDADES' | 'DOSE'
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('UN');
  const [fractionFactor, setFractionFactor] = useState(24);
  const [value, setValue] = useState(0);
  const [barCode, setBarCode] = useState('');
  const [particulars, setParticulars] = useState({});

  // Estados para modo dose / volume
  const [totalVolume, setTotalVolume] = useState(600);
  const [portionSize, setPortionSize] = useState(50);
  const [unitOfMeasure, setUnitOfMeasure] = useState('ml');

  const [errorName, setErrorName] = useState(false);
  const [errorFactor, setErrorFactor] = useState(false);
  const [errorValue, setErrorValue] = useState(false);
  const [errorParticulars, setErrorParticulars] = useState(false);

  const handleParticularChange = (key, val) => {
    setParticulars((prev) => ({
      ...prev,
      [key]: val,
    }));
    setErrorParticulars(false);
  };

  const handleCategoryChange = (newCat) => {
    setSelectedCategory(newCat);
    setParticulars({});
    setErrorParticulars(false);
  };

  useEffect(() => {
    if (!open) return;

    const detected = extractVolumeOrWeight(parentName);
    const numSuggestedFactor = suggestedFactor ? Number(suggestedFactor) : null;

    const catSource = initialData?.category || category;
    const resolvedCat = resolveCategory(catSource, allCategories);
    setSelectedCategory(resolvedCat);

    if (initialData) {
      setName(initialData.name || '');
      setUnit(initialData.unit ? normalizeUnitCode(initialData.unit) : 'UN');
      setFractionFactor(initialData.fractionFactor || (numSuggestedFactor && numSuggestedFactor > 1 ? numSuggestedFactor : 24));
      setValue(initialData.value || 0);
      setBarCode(initialData.barCode || '');
      setParticulars(safeParseParticulars(initialData.particulars) || {});

      if (initialData.unitOfMeasure) {
        setUnitOfMeasure(initialData.unitOfMeasure);
      } else if (detected) {
        setUnitOfMeasure(detected.unit);
      } else if (parentUnit && ['g', 'kg'].includes(parentUnit.toLowerCase())) {
        setUnitOfMeasure('g');
      } else {
        setUnitOfMeasure('ml');
      }

      if (numSuggestedFactor && numSuggestedFactor > 1) {
        setMode('UNIDADES');
      } else if (detected) {
        setTotalVolume(detected.total);
        if (initialData.fractionFactor > 0) {
          const inferredPortion = Math.round(detected.total / initialData.fractionFactor);
          setPortionSize(inferredPortion || 50);
        }
        setMode('DOSE');
      } else {
        setMode('UNIDADES');
      }
    } else {
      setValue(0);
      setBarCode('');
      setParticulars({});
      setErrorParticulars(false);

      if (detected) {
        setUnitOfMeasure(detected.unit);
      } else if (parentUnit && ['g', 'kg'].includes(parentUnit.toLowerCase())) {
        setUnitOfMeasure('g');
      } else {
        setUnitOfMeasure('ml');
      }

      // Se temos um fator sugerido pelo parser do fardo/caixa (ex: 12 FL, 6U, 24 un), prioridade MÁXIMA
      if (numSuggestedFactor && numSuggestedFactor > 1) {
        setMode('UNIDADES');
        setUnit('UN');
        setFractionFactor(numSuggestedFactor);
        const cleanName = (parentName || '')
          .replace(/\b\d{1,3}\s*(?:PACKS?|PKS?|PCKS?|FL|FILM|FILME|U|UN|UNID|LATAS?)\b/gi, '')
          .replace(/\b(?:CX|FD|PCT|LT|PET)\d{1,3}\b/gi, '')
          .replace(/\b(?:CX|FD|PCT|FARDO|CAIXA|PACK|FL)\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        setName(cleanName || parentName || 'Unidade Avulsa');
      } else if (detected) {
        setMode('DOSE');
        setUnit('DOSE');
        setTotalVolume(detected.total);
        setPortionSize(50);
        const calculatedFactor = parseFloat((detected.total / 50).toFixed(4));
        setFractionFactor(calculatedFactor);
        const suggestedName = parentName ? `${parentName} (Dose/Porção)` : '';
        setName(suggestedName);
      } else if (parentUnit && ['ml', 'l', 'lt', 'g', 'kg'].includes(parentUnit.toLowerCase())) {
        setMode('DOSE');
        setUnit('DOSE');
        setTotalVolume(1000);
        setPortionSize(50);
        setFractionFactor(20);
        const suggestedName = parentName ? `${parentName} (Dose/Porção)` : '';
        setName(suggestedName);
      } else {
        setMode('UNIDADES');
        setUnit('UN');
        setFractionFactor(24);
        const suggestedName = parentName ? `${parentName} (Unidade Avulsa)` : '';
        setName(suggestedName);
      }
    }
    setErrorName(false);
    setErrorFactor(false);
    setErrorValue(false);
  }, [initialData, parentName, open, suggestedFactor, category, allCategories]);

  // Recalcula fractionFactor quando mudar volume ou porção
  const handleVolumeChange = (newTotal, newPortion) => {
    const tot = parseFloat(newTotal) || 0;
    const por = parseFloat(newPortion) || 0;
    if (tot > 0 && por > 0) {
      const calculated = parseFloat((tot / por).toFixed(4));
      setFractionFactor(calculated);
    }
  };

  const factorNum = parseFloat(fractionFactor) || 0;
  const unitCost = factorNum > 0 ? (parentCost / factorNum) : 0;
  const unitPrice = parseFloat(value) || 0;
  const profit = unitPrice - unitCost;
  const margin = unitPrice > 0 ? ((profit / unitPrice) * 100).toFixed(1) : 0;

  const handleSave = () => {
    let hasError = false;
    if (!name.trim()) {
      setErrorName(true);
      hasError = true;
    }
    if (factorNum <= 0) {
      setErrorFactor(true);
      hasError = true;
    }
    if (unitPrice <= 0) {
      setErrorValue(true);
      hasError = true;
    }

    const cleanParticulars = {};
    const missingKeys = [];
    if (categoryParticularsDef && Object.keys(categoryParticularsDef).length > 0) {
      Object.keys(categoryParticularsDef).forEach((k) => {
        if (particulars[k] !== undefined && particulars[k] !== '') {
          cleanParticulars[k] = particulars[k];
        } else {
          missingKeys.push(k);
        }
      });
    }

    if (missingKeys.length > 0) {
      setErrorParticulars(true);
      hasError = true;
    }

    if (hasError) return;

    const catId = selectedCategory ? selectedCategory.id : (category?.id || (typeof category === 'number' ? category : null));
    const catName = selectedCategory ? selectedCategory.name : (category?.name || '');

    const formatUnit = normalizeUnitCode(unit || initialData?.unit || (mode === 'DOSE' ? 'DOSE' : 'UN'));

    onSave({
      ...(initialData || {}),
      name: name.trim(),
      fractionFactor: factorNum,
      cost: parseFloat(unitCost.toFixed(4)),
      value: unitPrice,
      unit: formatUnit,
      unitOfMeasure,
      barCode: barCode.trim(),
      category: catId,
      categoryName: catName,
      particulars: cleanParticulars,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <UIDialogTitle onClose={onClose}>
        {initialData ? 'Editar Formato de Venda' : 'Adicionar Formato de Venda Fracionado'}
      </UIDialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nome do formato de venda"
              placeholder="Ex: Lata Avulsa 350ml, Dose 50ml, Porção 100g"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errorName) setErrorName(false);
              }}
              error={errorName}
              helperText={errorName ? 'Informe o nome do formato' : 'Como este item será identificado no PDV ou catálogo'}
              fullWidth
              autoFocus
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Alternador de Modo de Rendimento */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' }}>
              Como calcular o rendimento da embalagem?
            </Typography>
            <Box className={classes.segmentedContainer}>
              <ButtonBase
                className={mode === 'DOSE' ? classes.segmentedButtonActive : classes.segmentedButton}
                onClick={() => {
                  setMode('DOSE');
                  if (unit === 'UN') setUnit('DOSE');
                  handleVolumeChange(totalVolume, portionSize);
                }}
              >
                Medida (ml / g)
              </ButtonBase>
              <ButtonBase
                className={mode === 'UNIDADES' ? classes.segmentedButtonActive : classes.segmentedButton}
                onClick={() => {
                  setMode('UNIDADES');
                  if (unit === 'DOSE') setUnit('UN');
                  if (suggestedFactor && Number(suggestedFactor) > 1) {
                    setFractionFactor(Number(suggestedFactor));
                  }
                }}
              >
                Unidade (Granel)
              </ButtonBase>
            </Box>
          </Grid>

          {mode === 'DOSE' ? (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Recipiente"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={totalVolume}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTotalVolume(val);
                    handleVolumeChange(val, portionSize);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unitOfMeasure}</InputAdornment>,
                  }}
                  helperText="Capacidade total"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Porção"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={portionSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPortionSize(val);
                    handleVolumeChange(totalVolume, val);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unitOfMeasure}</InputAdornment>,
                  }}
                  helperText="Qtd. por porção servida"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="unit-measure-label">Unidade de Medida</InputLabel>
                  <Select
                    labelId="unit-measure-label"
                    label="Unidade de Medida"
                    value={unitOfMeasure}
                    onChange={(e) => setUnitOfMeasure(e.target.value)}
                  >
                    <MenuItem value="ml">ml (Mililitro)</MenuItem>
                    <MenuItem value="g">g (Grama)</MenuItem>
                    <MenuItem value="L">L (Litro)</MenuItem>
                    <MenuItem value="kg">kg (Quilo)</MenuItem>
                    <MenuItem value="un">un (Unidade)</MenuItem>
                    {parentUnit && !['ml', 'g', 'l', 'lt', 'kg', 'un'].includes(parentUnit.toLowerCase()) && (
                      <MenuItem value={parentUnit.toLowerCase()}>{parentUnit}</MenuItem>
                    )}
                  </Select>
                  <FormHelperText>Unidade do recipiente</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box className={classes.formulaBox}>
                  <Typography variant="body2" style={{ color: '#0d47a1', fontWeight: 600 }}>
                    {totalVolume} {unitOfMeasure} ÷ {portionSize} {unitOfMeasure} = <strong>Rende {factorNum} porções</strong>
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ProductUnitSelect
                  variant="compact"
                  label="Unidade do Formato"
                  value={unit}
                  onChange={(newUnit) => setUnit(newUnit)}
                  helperText="Ex: DOSE, COP, TAÇA, UN"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CurrencyTextField
                  label={`Preço de venda (${unit || 'DOSE'})`}
                  variant="outlined"
                  size="small"
                  decimalCharacter=","
                  digitGroupSeparator="."
                  currencySymbol="R$"
                  outputFormat="number"
                  value={value}
                  onChange={(event, val) => {
                    setValue(val);
                    if (errorValue) setErrorValue(false);
                  }}
                  error={errorValue}
                  helperText={errorValue ? 'Preço deve ser maior que zero' : 'Valor cobrado do cliente no balcão/mesa'}
                  fullWidth
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Rendimento da embalagem"
                  placeholder="Ex: 24"
                  type="number"
                  inputProps={{ min: 1, step: 'any' }}
                  value={fractionFactor}
                  onChange={(e) => {
                    setFractionFactor(e.target.value);
                    if (errorFactor) setErrorFactor(false);
                  }}
                  error={errorFactor}
                  helperText={errorFactor ? 'Deve ser maior que zero' : 'Quantas unidades vêm na embalagem'}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ProductUnitSelect
                  variant="compact"
                  label="Unidade do Formato"
                  value={unit}
                  onChange={(newUnit) => setUnit(newUnit)}
                  helperText="Ex: UN, PC, LATA, FAT"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CurrencyTextField
                  label={`Preço por ${unit || 'unidade'}`}
                  variant="outlined"
                  size="small"
                  decimalCharacter=","
                  digitGroupSeparator="."
                  currencySymbol="R$"
                  outputFormat="number"
                  value={value}
                  onChange={(event, val) => {
                    setValue(val);
                    if (errorValue) setErrorValue(false);
                  }}
                  error={errorValue}
                  helperText={errorValue ? 'Preço deve ser maior que zero' : 'Valor cobrado do cliente'}
                  fullWidth
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box className={classes.formulaBox} style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <Typography variant="body2" style={{ color: '#166534', fontWeight: 600 }}>
                {`Resumo: 1 ${parentUnit || 'embalagem'} rende ${factorNum} ${unit || 'UN'} (Custo proporcional: ${formattedCurrency(unitCost)} cada)`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Código de barras deste formato (opcional)"
              placeholder="Bipe ou digite o código de barras da latinha/item avulso"
              value={barCode}
              onChange={(e) => setBarCode(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Se preenchido, bipar este código no caixa adicionará este formato diretamente"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <UIBarcodeRegenerateButton
                      itemId={initialData?.id}
                      itemType="product"
                      currentBarCode={barCode}
                      onGenerated={(newBarCode) => {
                        setBarCode(newBarCode);
                        if (initialData) {
                          initialData.barCode = newBarCode;
                        }
                        if (onBarcodeRegenerated) {
                          onBarcodeRegenerated(newBarCode);
                        }
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Categoria e Características da Variação */}
          <Grid item xs={12}>
            <Box
              style={{
                backgroundColor: '#f8fbff',
                border: '1px solid #d4e5f9',
                borderRadius: 8,
                padding: '14px',
              }}
            >
              <Typography
                variant="caption"
                style={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 10,
                  display: 'block',
                  color: '#17497F',
                }}
              >
                Categoria & Características da Variação
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    value={selectedCategory}
                    options={allCategories}
                    getOptionLabel={(option) => (option?.name ? option.name : '')}
                    getOptionSelected={(option, val) => String(option?.id) === String(val?.id)}
                    onChange={(_, newVal) => handleCategoryChange(newVal)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Categoria da Variação"
                        placeholder="Pesquise ou selecione a categoria..."
                        variant="outlined"
                        size="small"
                        helperText={
                          selectedCategory
                            ? `Categoria vinculada: ${selectedCategory.name}`
                            : 'Herda a categoria do produto principal se não selecionada'
                        }
                      />
                    )}
                  />
                </Grid>

                {categoryParticularsDef && Object.keys(categoryParticularsDef).length > 0 && (
                  Object.entries(categoryParticularsDef).map(([key, value]) => {
                    const currentVal = particulars[key] || '';
                    return (
                      <Grid key={key} item xs={12} sm={6}>
                        <FormControl
                          variant="outlined"
                          size="small"
                          fullWidth
                          required
                          error={Boolean(errorParticulars && !currentVal)}
                        >
                          <InputLabel id={`label-particular-${key}`}>{capitalize(key)} *</InputLabel>
                          <Select
                            labelId={`label-particular-${key}`}
                            label={`${capitalize(key)} *`}
                            value={currentVal}
                            onChange={(e) => handleParticularChange(key, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Selecione o(a) {key} *</em>
                            </MenuItem>
                            {Array.isArray(value)
                              ? value.map((vl) => (
                                <MenuItem key={vl} value={vl}>
                                  {vl}
                                </MenuItem>
                              ))
                              : value && (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              )}
                          </Select>
                          {Boolean(errorParticulars && !currentVal) && (
                            <FormHelperText error>Campo obrigatório</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );
                  })
                )}
              </Grid>
            </Box>
          </Grid>

          {/* Painel com cálculo inteligente */}
          <Grid item xs={12}>
            <Box className={classes.infoBox}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Resumo Econômico & Estoque
              </Typography>
              <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={8} mt={0.8}>
                <Chip
                  size="small"
                  label={`Custo Unitário: ${formattedCurrency(unitCost)}`}
                  style={{ backgroundColor: '#e8f0fe', color: '#1a73e8', fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  label={`Lucro Estimado: ${formattedCurrency(profit)} (${margin}%)`}
                  style={{
                    backgroundColor: profit >= 0 ? '#e6f4ea' : '#fce8e6',
                    color: profit >= 0 ? '#137333' : '#c5221f',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Consome 1/${formatStockAmount(factorNum || 1)} da embalagem`}
                />
              </Box>

              <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#1565c0', fontWeight: 500 }}>
                <strong>Estoque:</strong> Cada 1 item/embalagem no estoque renderá <strong>{formatStockAmount(factorNum)} porções</strong> disponíveis para venda.
              </Typography>

              {parentCost > 0 && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                  Baseado no custo de {formattedCurrency(parentCost)} da embalagem principal.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disableElevation>
          {initialData ? 'Salvar Alterações' : 'Adicionar Formato'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSaleFormatModal;
